import { ButtonHTMLAttributes, useMemo } from 'react';

import { Bars3Icon } from '@heroicons/react/24/outline';
import { IconButton } from '@material-tailwind/react';
import { VariantProps, cva } from 'class-variance-authority';
import Cookies from 'js-cookie';

import { api } from '@plugins/axios/api';

import { useRecoil } from '@hooks/useRecoil';

import { Button } from '@components/button/Button';
import { NavItem } from '@components/header/HeaderWrapper';
import { MNavLink } from '@components/link/MNavLink';

import { CSSVariables, cn } from '@utils/tailwind';

const DropAreaHeaderVariants = cva('', {
  variants: {},
  defaultVariants: {},
});

export interface DropAreaHeaderProps
  extends ButtonHTMLAttributes<HTMLHeadElement>,
    VariantProps<typeof DropAreaHeaderVariants> {
  logoImgSrc?: string;
  logoReverseImgSrc?: string;
  list: NavItem[];
  mMenuOpenFn: () => void;
}

export const DropAreaHeader = ({
  logoImgSrc,
  logoReverseImgSrc,
  list,
  className,
  mMenuOpenFn,
  ...props
}: DropAreaHeaderProps) => {
  const { useRecoilState, useAtom } = useRecoil();
  const [profile, setProfile] = useRecoilState(
    useAtom<{ uid: number; role: 'partner' | 'funeral'; name: string } | null>(
      'USER_PROFILE',
    ),
  );

  const CSSVariableStyle = useMemo(
    () =>
      CSSVariables({
        'head': {
          'tx-color': {
            'common': '#ffffff',
            'category': {
              active: '#ffd600',
              expend: '#111111',
              over: '#ffffff',
            },
            'category-item': { active: '#ffd600' },
          },
          'bg-color': {
            hover: '#111111',
            over: '#111111',
          },
        },
        'head-expend': {
          'bg-color': '#f7f7f7',
        },
      }),
    [],
  );

  const isOver = true;

  return (
    <header
      className={cn(
        isOver
          ? 'bg-[var(--head-bg-color-over)] transition-[padding] duration-100 ease-out'
          : 'bg-transparent transition-all duration-100 ease-out',
        'group/header fixed top-0 z-50 flex min-h-[theme(var.header.height.mobile)] w-[100vw] flex-col items-center justify-center border-b-[1px] border-none border-[#f7f7f7] shadow-none hover:bg-[var(--head-expend-bg-color)] xl:min-h-[theme(var.header.height.pc)] xl:hover:pt-[theme(var.header.height.pc)]',
        'after:fixed after:top-0 after:h-[theme(var.header.height.mobile)] after:w-[100vw] after:transition-none after:content-[""] hover:after:bg-[var(--head-bg-color-hover)]',
        className,
      )}
      style={CSSVariableStyle}
      {...props}
    >
      {/* pc 영역 */}
      <div className='container relative px-0'>
        <nav
          className={cn(
            'container fixed top-[0] z-50 hidden h-[theme(var.header.height.pc)] w-full flex-row items-center justify-center xl:flex',
          )}
        >
          <a
            className='absolute left-[5px] top-0 flex h-full w-[150px]'
            href='/'
          >
            <h1 className='my-auto pb-[6%] text-start text-2xl font-bold leading-[100%] tracking-tighter text-[#ffd600]'>
              Replhada
            </h1>
            <h3 className='mt-auto text-nowrap pb-[6%] text-start text-lg font-bold leading-[100%] tracking-tighter text-[#ffd600]'>
              <span className='pl-2 pr-2'>·</span>
              {profile != null
                ? profile.role == 'funeral'
                  ? '장례식장'
                  : profile.role == 'partner'
                    ? '배달파트너'
                    : ''
                : ''}
            </h3>
          </a>
          <div className='flex max-w-[55%] flex-1 flex-col'>
            <ul
              className={cn(
                'flex h-fit flex-1 flex-row justify-between text-white',
                'group-hover/header:text-black',
              )}
            >
              {list.map((item, i) => {
                return (
                  <li
                    key={`nav-item-${i}`}
                    className='relative flex flex-1 flex-col'
                  >
                    <div className='flex h-[theme(var.header.height.pc)] w-full justify-center px-[10%] transition-none'>
                      <MNavLink
                        to={item.link ?? '/'}
                        className={cn(
                          'font-leading-[1.1rem] my-auto h-fit cursor-pointer flex-row whitespace-pre-wrap text-center align-middle text-sm group-hover/header:font-bold group-hover/header:drop-shadow-none lg:text-[1.05rem]',
                          isOver
                            ? 'font-bold text-[var(--head-tx-color-category-over)] drop-shadow-none'
                            : 'font-normal text-inherit drop-shadow-lg',
                        )}
                        isActiveClassName={(isActive) =>
                          isActive
                            ? 'text-[var(--head-tx-color-category-active)]'
                            : ''
                        }
                        {...item}
                      >
                        {item.title}
                      </MNavLink>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className='absolute right-[5px] top-0 flex h-full w-[200px] items-center justify-end space-x-3'>
            <h3 className='text-white'>
              {profile != null ? profile.name : ''}
            </h3>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                api.post('/auth/logout').then(() => {
                  Cookies.remove('REPLHADA_ADMIN_ACCESS_TOKEN');
                  Cookies.remove('REPLHADA_ADMIN_REFRESH_TOKEN');
                  setProfile(null);
                });
              }}
            >
              로그아웃
            </Button>
          </div>
        </nav>
      </div>
      {/* pc 영역 서브 메뉴 */}
      <div
        className={cn(
          'container relative hidden h-full w-full flex-row items-center justify-center xl:flex',
        )}
      >
        <ul
          className={cn(
            'flex h-fit max-w-[55%] flex-1 flex-row justify-between text-white',
            'group-hover/header:text-black',
          )}
        >
          {list.map((item, i) => {
            return (
              <li
                key={`nav-item-${i}`}
                className='relative flex flex-1 flex-col'
              >
                {item.list && item.list.length != 0 && (
                  <ul className='hidden w-full flex-1 cursor-default flex-col py-6 text-sm leading-[1.1rem] group-hover/header:flex lg:ml-[32%] lg:text-[0.95rem]'>
                    {item.list.map((sItem, si) => {
                      return (
                        <li
                          key={`nav-item-${i}-sItem-${si}`}
                          className='my-[8px] flex w-full cursor-pointer justify-start text-[#111111] drop-shadow-sm'
                        >
                          <MNavLink
                            to={
                              sItem.link
                                ? item.link + '/' + sItem.link
                                : (item.link ?? '/')
                            }
                            className={cn(
                              'my-auto flex h-fit cursor-pointer flex-row whitespace-pre-wrap text-center align-middle font-light',
                              'before:mx-2 before:my-auto before:flex before:h-[5px] before:w-[5px] before:rounded-2xl before:bg-[var(--head-tx-color-category-item-active)] before:content-[""]',
                            )}
                            isActiveClassName={(isActive) =>
                              isActive
                                ? 'before:visible underline underline-offset-2 decoration-[var(--head-tx-color-category-item-active)] decoration-4'
                                : 'before:invisible'
                            }
                            {...sItem}
                          >
                            {sItem.title}
                          </MNavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className='container z-50 flex h-full w-full flex-col'>
        {/* 모바일 영역 */}
        <nav className='flex h-full w-full flex-row items-center justify-between xl:hidden'>
          <div className='flex h-[30px] w-[110px] lg:w-[130px]'>
            <h1 className='my-auto pb-[6%] text-start text-2xl font-bold leading-[100%] tracking-tighter text-[#ffd600]'>
              Replhada
            </h1>
            <h3 className='mt-auto text-nowrap pb-[6%] text-start text-lg font-bold leading-[100%] tracking-tighter text-[#ffd600]'>
              <span className='pl-2 pr-2'>·</span>
              {profile != null
                ? profile.role == 'funeral'
                  ? '장례식장'
                  : profile.role == 'partner'
                    ? '배달파트너'
                    : ''
                : ''}
            </h3>
          </div>
          <IconButton variant='text' color='white' onClick={mMenuOpenFn}>
            <Bars3Icon className='h-11 w-10 text-white' />
          </IconButton>
        </nav>
      </div>
    </header>
  );
};
